"use client";

import React, {useEffect, useState} from "react";
import {PagePayoutSchema} from "@/sanity/pages/PagePayoutTemplate";

import {cn} from "@/lib/utils";
import Image from "next/image";
import {PortableText} from "@portabletext/react";
import {richTextComponent} from "@/sanity/sanityPortableText";
import ChipSerif from "@/components/global/ui/ChipSerif";
import {Carousel, type CarouselApi, CarouselContent, CarouselItem} from "@/shadcn/ui/carousel";

import bg from "@/public/global/bg-art.jpg";

export default function StoriesSection({
  data,
  className
}: {
  data: PagePayoutSchema;
  className?: string;
}) {
  const isBrazil = data?.settings?.slug?.current?.endsWith("/br");
  const startIndex = data?.storiesItems ? Math.floor(data.storiesItems.length / 2) : 0;
  const [api, setApi] = useState<CarouselApi>();
  const [current, setCurrent] = useState(startIndex);
  const [canPrev, setCanPrev] = useState(false);
  const [canNext, setCanNext] = useState(false);

  useEffect(() => {
    if (!api) return;

    const onSelect = () => {
      setCurrent(api.selectedScrollSnap());
      setCanPrev(api.canScrollPrev());
      setCanNext(api.canScrollNext());
    };

    onSelect();
    api.on("select", onSelect);
    api.on("reInit", onSelect);
    return () => {
      api.off("select", onSelect);
      api.off("reInit", onSelect);
    };
  }, [api]);

  return (
    <section className={cn(
      "relative overflow-clip px-[2rem] lg:px-[5rem]",
      className
    )}>
      <div className="relative overflow-clip w-[min(229.25rem,100%)] mx-auto rounded-[2rem] lg:rounded-[5rem] flex flex-col items-center pt-[6rem] lg:pt-[17.5rem] pb-[8rem] lg:pb-[27.5rem]">
        <div className="absolute top-0 left-0 w-full h-full bg-[#E6EDE9]">
          <Image
            className="absolute left-1/2 -translate-x-1/2 bottom-0 !w-[max(100%,90rem)] !max-w-none !h-full object-cover object-top mix-blend-luminosity opacity-10"
            src={bg}
            quality={100}
            alt="background art"
          />
        </div>

        <div className="container flex flex-col items-center relative">
          {data?.storiesChip && (
            <ChipSerif text={data.storiesChip}/>
          )}

          {data?.storiesText && (
            <div className="richtext mb-[4rem] lg:mb-[6rem]">
              <PortableText
                value={data.storiesText}
                components={richTextComponent}
              />
            </div>
          )}
        </div>

        {data?.storiesItems && (
          <div className="w-full relative lg:[mask-image:linear-gradient(to_right,transparent_0%,transparent_2%,black_15%,black_85%,transparent_98%,transparent_100%)] lg:[-webkit-mask-image:linear-gradient(to_right,transparent_0%,transparent_2%,black_15%,black_85%,transparent_98%,transparent_100%)]">
            <div className="relative w-[min(120rem,100%)] mx-auto">
              <Carousel
                className="[&_>_div]:!overflow-visible w-full px-[2rem]"
                setApi={setApi}
                opts={{
                  align: "center",
                  skipSnaps: false,
                  startIndex: startIndex,
                }}
              >
                <CarouselContent className="w-full gap-x-[2rem] lg:gap-x-[10rem]">
                  {data.storiesItems.map((item, index) => {
                    const isActive = index === current;
                    return (
                      <CarouselItem
                        key={index}
                        className={cn(
                          "basis-[42rem] lg:basis-[117.875rem] p-[3rem] lg:py-[6.5rem] lg:px-[7rem] flex flex-col lg:flex-row items-start lg:items-center gap-[6rem] lg:gap-[8rem] rounded-[2rem] transition-colors duration-300",
                          isActive ? "bg-[#103037]" : "bg-white"
                        )}
                      >
                        <div className="flex flex-col items-start w-full sm:w-[27rem] flex-shrink-0">
                          {item?.person?.image && (
                            <Image
                              className="!w-full aspect-square object-cover rounded-[1.5rem] mb-[2rem]"
                              src={item.person.image}
                              quality={95}
                              alt=""
                            />
                          )}

                          <p className={cn(
                            "mb-[1rem] text-[2.75rem] leading-none font-instrument transition-colors duration-300",
                            isActive ? "text-white" : "text-black"
                          )}>
                            {item?.person?.name}
                          </p>
                          <p className={cn(
                            "mb-0 text-[1.875rem] leading-none font-instrument font-light transition-colors duration-300",
                            isActive ? "text-white/50" : "text-black/50"
                          )}>
                            {item?.person?.subtext}
                          </p>
                        </div>

                        <div className="flex flex-col gap-[3rem] lg:gap-[6rem]">
                          {item?.question && (
                            <div className="flex flex-col items-start gap-[1rem]">
                              <p className={cn(
                                "mb-0 font-medium text-[1.5rem] leading-none transition-colors duration-300",
                                isActive ? "text-white/70" : "text-black/40"
                              )}>
                                {isBrazil ? "Pergunta:" : "QUESTION:"}
                              </p>

                              <div className={cn(
                                "richtext richtext-start prose-p:tracking-tight prose-p:font-instrument-sans prose-p:font-semibold prose-p:text-[2rem] transition-colors duration-300",
                                isActive ? "prose-p:text-white" : "prose-p:text-black/40"
                              )}>
                                <PortableText
                                  value={item.question}
                                  components={richTextComponent}
                                />
                              </div>
                            </div>
                          )}

                          {item?.answer && (
                            <div className="flex flex-col items-start gap-[1rem]">
                              <p className={cn(
                                "mb-0 font-medium text-[1.5rem] leading-none transition-colors duration-300",
                                isActive ? "text-white/70" : "text-black/40"
                              )}>
                                {isBrazil ? "Resposta:" : "ANSWER:"}
                              </p>

                              <div className={cn(
                                "richtext richtext-start prose-p:tracking-tight prose-p:font-instrument-sans prose-p:font-semibold prose-p:text-[2.25rem] lg:prose-p:text-[3rem] transition-colors duration-300",
                                isActive ? "prose-p:text-white" : "prose-p:text-black"
                              )}>
                                <PortableText
                                  value={item.answer}
                                  components={richTextComponent}
                                />
                              </div>
                            </div>
                          )}
                        </div>
                      </CarouselItem>
                    );
                  })}
                </CarouselContent>
              </Carousel>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
